import { useEffect, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || '';

export interface SimulationEstimate {
  baseMinutes: number;
  queueMinutes: number;
  totalMinutes: number;
  queuedCount: number;
}

interface SimulationLike {
  content?: string | null;
  personas?: unknown[];
  use_case?: string;
  [key: string]: unknown;
}

function calcBaseMinutes(simulation: SimulationLike): number {
  let contentData: Record<string, unknown> = {};
  if (simulation.content) {
    try { contentData = JSON.parse(simulation.content as string); } catch { /* ignore */ }
  }

  const numPersonas = simulation.personas?.length || 0;
  const numQuestions = (contentData.num_questions as number) || (contentData.questions as unknown[])?.length || 0;
  const numImages = (contentData.images as unknown[])?.length || 0;
  const task = (contentData.task as string) || simulation.use_case || '';

  if (task === 'driving-concept-test') {
    const sampleSize = (contentData.sample_size as number) || 10;
    return Math.max(1, Math.ceil(sampleSize / 5));
  }

  if (task === 'vitamin-concept-test') {
    const sampleSize = (contentData.sample_size as number) || 126;
    const numConcepts = (contentData.concepts as unknown[])?.length || 1;
    return Math.max(1, Math.ceil(3 * (sampleSize / 100) * numConcepts));
  }

  if (task === 'packaging-review-v2') {
    const taskData = (contentData.data as Record<string, unknown>) || {};
    const analysisType = (taskData.analysis_type as string) || 'all';
    const hasSideImages = Array.isArray(taskData.side_images) && taskData.side_images.length > 0;
    const hasBackImages = Array.isArray(taskData.back_images) && taskData.back_images.length > 0;
    const PACKAGING_BASE: Record<string, number> = {
      all: 10, shelf_test: 3, technical: 3,
      persona_reaction: 3, side_panel: 3, back_panel: 3, element_drivers: 3,
    };
    let base = PACKAGING_BASE[analysisType] ?? 10;
    if (numPersonas > 100) base += Math.ceil((numPersonas - 100) / 50);
    if (hasSideImages) base += 2;
    if (hasBackImages) base += 2;
    return base;
  }

  if (task === 'packaging-variant-comparison') {
    const taskData = (contentData.data as Record<string, unknown>) || {};
    const numVariants = Array.isArray(taskData.variant_images) ? taskData.variant_images.length : 2;
    return 8 + Math.max(0, numVariants - 2) * 4;
  }

  // Generic: ~2 min per 100 personas * items
  const items = numQuestions || numImages || 1;
  const perPersonaMinutes = Math.max(2, Math.ceil(2 * numPersonas / 100));
  return perPersonaMinutes * items * 1.5;
}

export function useSimulationTimeEstimate(
  simulationId: string | number | null,
  isRunning: boolean,
) {
  const [estimate, setEstimate] = useState<SimulationEstimate | null>(null);
  const [remainingMinutes, setRemainingMinutes] = useState<number | null>(null);

  useEffect(() => {
    if (!simulationId || !isRunning) return;

    let cancelled = false;
    (async () => {
      try {
        const [simRes, queueRes] = await Promise.all([
          fetch(`${API_URL}/simulations/${simulationId}`, { credentials: 'include' }),
          fetch(`${API_URL}/simulations/queue-status`, { credentials: 'include' }),
        ]);
        if (cancelled) return;

        const simulation: SimulationLike = simRes.ok ? await simRes.json() : {};
        const queueData = queueRes.ok ? await queueRes.json() : { queued_count: 0, worker_count: 1 };

        const baseMinutes = calcBaseMinutes(simulation);
        let queueMinutes = 0;
        if (queueData.queued_count > 0) {
          queueMinutes = Math.ceil((queueData.queued_count / Math.max(1, queueData.worker_count)) * 4);
        }
        const totalMinutes = baseMinutes + queueMinutes;

        if (!cancelled) {
          setEstimate({ baseMinutes, queueMinutes, totalMinutes, queuedCount: queueData.queued_count });
          setRemainingMinutes(totalMinutes);
        }
      } catch {
        if (!cancelled) {
          setEstimate({ baseMinutes: 2, queueMinutes: 0, totalMinutes: 2, queuedCount: 0 });
          setRemainingMinutes(2);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [simulationId, isRunning]);

  // Countdown
  useEffect(() => {
    if (remainingMinutes === null || remainingMinutes <= 0 || !isRunning) return;
    const timer = setInterval(() => {
      setRemainingMinutes(prev => (prev !== null && prev > 0) ? prev - 1 : 0);
    }, 60_000);
    return () => clearInterval(timer);
  }, [remainingMinutes, isRunning]);

  // Reset when simulation changes
  useEffect(() => {
    setEstimate(null);
    setRemainingMinutes(null);
  }, [simulationId]);

  return { estimate, remainingMinutes };
}
